import {
  PROPERTY_CUSTOMER_ROLES,
  type PropertyCustomerRole,
} from "../../../../../../../../../../../../../_shared/_logic/enums"
import type {
  AssignRequesterCustomerPreviewPayload,
  AssignRequesterCustomerRole,
  RequesterCustomerPreviewCandidate,
} from "../contract"

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown, path: string): JsonObject => {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error(`${path} muss ein Objekt sein.`)

  return value as JsonObject
}

const exactKeys = (value: JsonObject, allowed: readonly string[], path: string) => {
  const unexpected = Object.keys(value).filter((key) => !allowed.includes(key))

  if (unexpected.length)
    throw new Error(`${path} enthält unbekannte Felder: ${unexpected.join(", ")}.`)
}

const stringValue = (value: unknown, path: string): string => {
  if (typeof value !== "string" || !value.trim())
    throw new Error(`${path} muss ein nicht-leerer String sein.`)

  return value.trim()
}

const nullableString = (value: unknown, path: string): string => {
  if (value === null || value === undefined) return ""

  if (typeof value !== "string") throw new Error(`${path} muss String oder null sein.`)

  return value.trim()
}

const nullableId = (value: unknown, path: string): string | null =>
  nullableString(value, path) || null

const roleValue = (value: unknown, path: string): AssignRequesterCustomerRole => {
  const role = nullableString(value, path).toUpperCase()

  if (!role) return ""

  if (!PROPERTY_CUSTOMER_ROLES.includes(role as PropertyCustomerRole)) {
    throw new Error(`${path} enthält eine unbekannte Ansprechpartnerrolle.`)
  }

  return role as AssignRequesterCustomerRole
}

const stringArray = (value: unknown, path: string): string[] => {
  if (!Array.isArray(value)) throw new Error(`${path} muss ein Array sein.`)

  return value.map((entry, index) => stringValue(entry, `${path}.${index}`))
}

const candidateValue = (value: unknown, path: string): RequesterCustomerPreviewCandidate => {
  const candidate = objectValue(value, path)
  exactKeys(candidate, ["customer_id", "display_name", "email", "phone"], path)

  return {
    customerId: stringValue(candidate.customer_id, `${path}.customer_id`),
    displayName: stringValue(candidate.display_name, `${path}.display_name`),
    email: nullableString(candidate.email, `${path}.email`),
    phone: nullableString(candidate.phone, `${path}.phone`),
  }
}

const candidateRolesValue = (
  value: unknown,
  path: string,
): Record<string, AssignRequesterCustomerRole> => {
  const roles = objectValue(value, path)

  return Object.fromEntries(
    Object.entries(roles).map(([customerId, role]) => {
      if (!customerId.trim() || customerId.trim() !== customerId) {
        throw new Error(`${path} enthält eine ungültige Kunden-ID.`)
      }

      return [customerId, roleValue(role, `${path}.${customerId}`)]
    }),
  )
}

export const parseAssignRequesterCustomerPreview = (
  payload: unknown,
): AssignRequesterCustomerPreviewPayload => {
  const root = objectValue(payload, "payload")
  exactKeys(
    root,
    ["row_context_by_action_case_id", "row_errors_by_action_case_id", "shared"],
    "payload",
  )
  const rows = objectValue(
    root.row_context_by_action_case_id,
    "payload.row_context_by_action_case_id",
  )
  const errors = objectValue(
    root.row_errors_by_action_case_id,
    "payload.row_errors_by_action_case_id",
  )
  const shared = objectValue(root.shared, "payload.shared")
  exactKeys(shared, ["customer_candidates_by_id"], "payload.shared")
  const candidates = objectValue(
    shared.customer_candidates_by_id,
    "payload.shared.customer_candidates_by_id",
  )

  const customerCandidatesById = Object.fromEntries(
    Object.entries(candidates).map(([customerId, value]) => {
      const candidate = candidateValue(
        value,
        `payload.shared.customer_candidates_by_id.${customerId}`,
      )

      if (candidate.customerId !== customerId) {
        throw new Error(`Kandidat ${customerId} referenziert eine abweichende customer_id.`)
      }

      return [customerId, candidate]
    }),
  )

  const rowContextByActionCaseId = Object.fromEntries(
    Object.entries(rows).map(([actionCaseId, value]) => {
      const row = objectValue(value, `payload.row_context_by_action_case_id.${actionCaseId}`)
      exactKeys(
        row,
        [
          "property_id",
          "property_unit_id",
          "candidate_customer_ids",
          "candidate_roles_by_customer_id",
          "selected_requester_customer_id",
          "submitted_name",
          "submitted_role",
        ],
        `payload.row_context_by_action_case_id.${actionCaseId}`,
      )

      return [
        actionCaseId,
        {
          propertyId: nullableId(
            row.property_id,
            `payload.row_context_by_action_case_id.${actionCaseId}.property_id`,
          ),
          propertyUnitId: nullableId(
            row.property_unit_id,
            `payload.row_context_by_action_case_id.${actionCaseId}.property_unit_id`,
          ),
          candidateCustomerIds: stringArray(
            row.candidate_customer_ids,
            `payload.row_context_by_action_case_id.${actionCaseId}.candidate_customer_ids`,
          ),
          candidateRolesByCustomerId: candidateRolesValue(
            row.candidate_roles_by_customer_id,
            `payload.row_context_by_action_case_id.${actionCaseId}.candidate_roles_by_customer_id`,
          ),
          selectedRequesterCustomerId: nullableId(
            row.selected_requester_customer_id,
            `payload.row_context_by_action_case_id.${actionCaseId}.selected_requester_customer_id`,
          ),
          submittedName: nullableString(
            row.submitted_name,
            `payload.row_context_by_action_case_id.${actionCaseId}.submitted_name`,
          ),
          submittedRole: roleValue(
            row.submitted_role,
            `payload.row_context_by_action_case_id.${actionCaseId}.submitted_role`,
          ),
        },
      ]
    }),
  )
  for (const [actionCaseId, row] of Object.entries(rowContextByActionCaseId)) {
    for (const [index, customerId] of row.candidateCustomerIds.entries()) {
      if (!customerCandidatesById[customerId]) {
        throw new Error(
          `payload.row_context_by_action_case_id.${actionCaseId}.candidate_customer_ids.${index} referenziert die fehlende ID ${customerId}.`,
        )
      }

      if (!Object.hasOwn(row.candidateRolesByCustomerId, customerId)) {
        throw new Error(
          `payload.row_context_by_action_case_id.${actionCaseId}.candidate_roles_by_customer_id enthält keine Zuordnung für ${customerId}.`,
        )
      }
    }
    for (const customerId of Object.keys(row.candidateRolesByCustomerId)) {
      if (!row.candidateCustomerIds.includes(customerId)) {
        throw new Error(
          `payload.row_context_by_action_case_id.${actionCaseId}.candidate_roles_by_customer_id referenziert die nicht ausgewiesene ID ${customerId}.`,
        )
      }
    }

    if (
      row.selectedRequesterCustomerId &&
      !customerCandidatesById[row.selectedRequesterCustomerId]
    ) {
      throw new Error(
        `payload.row_context_by_action_case_id.${actionCaseId}.selected_requester_customer_id referenziert die fehlende ID ${row.selectedRequesterCustomerId}.`,
      )
    }

    if (
      row.selectedRequesterCustomerId &&
      !row.candidateCustomerIds.includes(row.selectedRequesterCustomerId)
    ) {
      throw new Error(
        `payload.row_context_by_action_case_id.${actionCaseId}.selected_requester_customer_id ist nicht in candidate_customer_ids enthalten.`,
      )
    }
  }

  return {
    rowContextByActionCaseId,
    rowErrorsByActionCaseId: Object.fromEntries(
      Object.entries(errors).map(([actionCaseId, value]) => [
        actionCaseId,
        stringValue(value, `payload.row_errors_by_action_case_id.${actionCaseId}`),
      ]),
    ),
    customerCandidatesById,
  }
}
